(() => {
/**
 * Full-screen viewer for a history gallery: progressive high-res loading, counter, filmstrip,
 * keyboard (arrows, Home/End, Z, Esc), swipe, click-to-zoom with pointer panning, and an
 * opening transition that grows the photo out of the thumbnail that was clicked.
 */
class HistoryLightbox {
  #dialog;
  #stage;
  #image;
  #counter;
  #strip;
  #images;
  #title;
  #labels;
  #onClose;
  #index = 0;
  #zoomed = false;
  #pointer = null;
  #reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  constructor({ title, images, labels, onClose }) {
    this.#title = title;
    this.#images = images.filter(Boolean);
    this.#labels = labels;
    this.#onClose = onClose;
    this.#build();
  }

  static sized(url, width) {
    try {
      const sizedUrl = new URL(url, window.location.href);
      sizedUrl.searchParams.set('width', width);
      return sizedUrl.href;
    } catch {
      return url;
    }
  }

  #build() {
    const dialog = document.createElement('dialog');
    dialog.className = 'history-lightbox';
    dialog.setAttribute('aria-label', this.#title || 'Image gallery');
    const thumbs = this.#images
      .map((img, i) => `<button type="button" class="history-lightbox__thumb" data-index="${i}" aria-label="${i + 1} / ${this.#images.length}"><img src="${HistoryLightbox.sized(img.currentSrc || img.src, 240)}" alt="" loading="lazy"></button>`)
      .join('');
    dialog.innerHTML = `
      <header class="history-lightbox__bar">
        <p class="history-lightbox__title"></p>
        <p class="history-lightbox__counter" aria-live="polite"></p>
        <button type="button" class="history-lightbox__close" aria-label="${this.#labels.close}">
          <svg viewBox="0 0 12 12" width="14" height="14" aria-hidden="true"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
        </button>
      </header>
      <div class="history-lightbox__stage">
        <button type="button" class="history-lightbox__nav history-lightbox__nav--previous" aria-label="${this.#labels.previous}">
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <figure class="history-lightbox__figure">
          <img class="history-lightbox__image" alt="" draggable="false">
          <span class="history-lightbox__loader" aria-hidden="true"></span>
        </figure>
        <button type="button" class="history-lightbox__nav history-lightbox__nav--next" aria-label="${this.#labels.next}">
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </div>
      ${this.#images.length > 1 ? `<nav class="history-lightbox__strip" aria-label="Thumbnails">${thumbs}</nav>` : ''}
    `;
    document.body.append(dialog);

    this.#dialog = dialog;
    this.#stage = dialog.querySelector('.history-lightbox__figure');
    this.#image = dialog.querySelector('.history-lightbox__image');
    this.#counter = dialog.querySelector('.history-lightbox__counter');
    this.#strip = dialog.querySelector('.history-lightbox__strip');
    dialog.querySelector('.history-lightbox__title').textContent = this.#title || '';
    if (this.#images.length < 2) dialog.classList.add('is-single');

    dialog.querySelector('.history-lightbox__close').addEventListener('click', () => this.close());
    dialog.querySelector('.history-lightbox__nav--previous').addEventListener('click', () => this.step(-1));
    dialog.querySelector('.history-lightbox__nav--next').addEventListener('click', () => this.step(1));
    this.#strip?.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-index]');
      if (thumb) this.go(Number(thumb.dataset.index));
    });

    dialog.addEventListener('keydown', (event) => {
      const keys = { ArrowLeft: () => this.step(-1), ArrowRight: () => this.step(1), Home: () => this.go(0), End: () => this.go(this.#images.length - 1), z: () => this.#toggleZoom(), Z: () => this.#toggleZoom() };
      if (!keys[event.key]) return;
      event.preventDefault();
      keys[event.key]();
    });
    // Click on the dark surround closes; the native Esc close is tidied up in the close event
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.classList.contains('history-lightbox__stage')) this.close();
    });
    dialog.addEventListener('close', () => this.#afterClose());

    this.#bindPointer();
  }

  // Swipe to change image; when zoomed, the pointer pans instead
  #bindPointer() {
    const stage = this.#stage;
    let lastTap = 0;
    stage.addEventListener('pointerdown', (event) => {
      this.#pointer = { x: event.clientX, y: event.clientY, moved: false, type: event.pointerType };
    });
    stage.addEventListener('pointermove', (event) => {
      if (this.#zoomed) {
        this.#pan(event.clientX, event.clientY);
        return;
      }
      if (this.#pointer && Math.abs(event.clientX - this.#pointer.x) > 8) this.#pointer.moved = true;
    });
    stage.addEventListener('pointerup', (event) => {
      if (!this.#pointer) return;
      const dx = event.clientX - this.#pointer.x;
      const { moved, type } = this.#pointer;
      this.#pointer = null;
      if (!this.#zoomed && moved && Math.abs(dx) > 50) {
        this.step(dx < 0 ? 1 : -1);
        return;
      }
      if (moved) return;
      // Mouse: single click zooms. Touch: double-tap zooms, so a single tap can't trap the view.
      if (type === 'mouse' || type === 'pen') {
        this.#toggleZoom(event.clientX, event.clientY);
      } else {
        const now = Date.now();
        if (now - lastTap < 320) this.#toggleZoom(event.clientX, event.clientY);
        lastTap = now;
      }
    });
  }

  #toggleZoom(x, y) {
    this.#zoomed = !this.#zoomed;
    this.#dialog.classList.toggle('is-zoomed', this.#zoomed);
    if (this.#zoomed) {
      const box = this.#image.getBoundingClientRect();
      this.#pan(x ?? box.left + box.width / 2, y ?? box.top + box.height / 2);
    } else {
      this.#image.style.transformOrigin = '';
    }
  }

  #pan(x, y) {
    const box = this.#stage.getBoundingClientRect();
    const px = Math.min(Math.max((x - box.left) / box.width, 0), 1) * 100;
    const py = Math.min(Math.max((y - box.top) / box.height, 0), 1) * 100;
    this.#image.style.transformOrigin = `${px}% ${py}%`;
  }

  open(index, fromImage) {
    this.#index = index;
    this.#render(0);
    document.documentElement.classList.add('history-lightbox-open');
    this.#dialog.showModal();
    this.#dialog.querySelector('.history-lightbox__close').focus({ preventScroll: true });

    if (this.#reduced.matches) return;
    this.#dialog.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 320, easing: 'ease-out' });
    // Grow the photo out of the thumbnail that was clicked
    const from = fromImage?.getBoundingClientRect();
    requestAnimationFrame(() => {
      const to = this.#image.getBoundingClientRect();
      if (!from || !to.width) return;
      const dx = from.left + from.width / 2 - (to.left + to.width / 2);
      const dy = from.top + from.height / 2 - (to.top + to.height / 2);
      const scale = from.width / to.width;
      this.#image.animate(
        [{ transform: `translate(${dx}px, ${dy}px) scale(${scale})`, borderRadius: '24px' }, { transform: 'none' }],
        { duration: 520, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
      );
    });
  }

  close() {
    if (!this.#dialog.open) return;
    if (this.#reduced.matches) {
      this.#dialog.close();
      return;
    }
    this.#dialog.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, easing: 'ease-in' }).onfinish = () => this.#dialog.close();
  }

  #afterClose() {
    document.documentElement.classList.remove('history-lightbox-open');
    if (this.#zoomed) this.#toggleZoom();
    this.#onClose?.();
  }

  step(direction) {
    this.go((this.#index + direction + this.#images.length) % this.#images.length, direction);
  }

  go(index, direction = index > this.#index ? 1 : -1) {
    if (index === this.#index || index < 0 || index >= this.#images.length) return;
    if (this.#zoomed) this.#toggleZoom();
    this.#index = index;
    this.#render(direction);
  }

  #render(direction) {
    const source = this.#images[this.#index];
    const base = source.currentSrc || source.src;
    const wanted = Math.min(2400, Math.ceil(window.innerWidth * (window.devicePixelRatio || 1) / 300) * 300);
    const full = HistoryLightbox.sized(base, wanted);

    // Show what is already loaded at once, then swap in the full-resolution file
    this.#image.src = base;
    this.#image.alt = source.alt || '';
    this.#dialog.classList.add('is-loading');
    const token = Symbol();
    this.#loading = token;
    const hiRes = new Image();
    hiRes.onload = () => {
      if (this.#loading !== token) return;
      this.#image.src = full;
      this.#dialog.classList.remove('is-loading');
    };
    hiRes.onerror = () => this.#dialog.classList.remove('is-loading');
    hiRes.src = full;

    // Warm the neighbours so stepping feels instant
    for (const offset of [1, -1]) {
      const neighbour = this.#images[(this.#index + offset + this.#images.length) % this.#images.length];
      if (neighbour) new Image().src = HistoryLightbox.sized(neighbour.currentSrc || neighbour.src, wanted);
    }

    this.#counter.textContent = this.#images.length > 1 ? `${this.#index + 1} / ${this.#images.length}` : '';
    this.#strip?.querySelectorAll('[data-index]').forEach((thumb, i) => {
      const current = i === this.#index;
      thumb.classList.toggle('is-current', current);
      if (current) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: this.#reduced.matches ? 'auto' : 'smooth' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    if (direction && !this.#reduced.matches) {
      this.#image.animate(
        [{ opacity: 0.2, transform: `translateX(${direction * 3}rem) scale(0.98)` }, { opacity: 1, transform: 'none' }],
        { duration: 420, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
      );
    }
  }

  #loading = null;

  destroy() {
    this.#dialog.remove();
    document.documentElement.classList.remove('history-lightbox-open');
  }
}

class HistoryGallery extends HTMLElement {
  #track;
  #items = [];
  #modalItems = [];
  #modalIndex = 0;
  #raf = null;
  #position = 0;
  #resetPoint = 0;
  #paused = false;
  #motion = null;
  #mobileQuery = window.matchMedia('(max-width: 767px)');
  #dialog;
  #lightbox = null;

  connectedCallback() {
    requestAnimationFrame(() => this.#initialize());
  }

  #initialize() {
    this.#track = this.querySelector('.history-gallery__track');
    if (!this.#track || this.dataset.historyGalleryReady === 'true') return;

    this.dataset.historyGalleryReady = 'true';
    this.#items = Array.from(this.querySelectorAll('.history-gallery__item'));
    this.#bindEvents();
    this.#setMode();
    this.#setupPhoneNav();
  }

  // Phones (curtis-mobile-pages.css): the duplicate loop copies are hidden, the buttons sit under the row,
  // and a "2 / 5" count says where you are. Each button press moves exactly one photo.
  #phoneQuery = window.matchMedia('(max-width: 749px)');
  #count = null;

  #uniqueItems() {
    return this.#items.filter((item) => item.getAttribute('aria-hidden') !== 'true');
  }

  #phoneIndex() {
    const items = this.#uniqueItems();
    const left = this.#track.scrollLeft;
    let best = 0;
    items.forEach((item, i) => {
      if (Math.abs(item.offsetLeft - items[0].offsetLeft - left) < Math.abs(items[best].offsetLeft - items[0].offsetLeft - left)) best = i;
    });
    // At the end of the row the last photo can't scroll to the left edge, so count it once fully in view
    if (left >= this.#track.scrollWidth - this.#track.clientWidth - 2) best = items.length - 1;
    return best;
  }

  #syncPhoneNav = () => {
    this.#syncFrame = 0;
    const items = this.#uniqueItems();
    const index = this.#phoneIndex();
    if (this.#count) this.#count.textContent = `${index + 1} / ${items.length}`;
    const previous = this.querySelector('[data-history-gallery-previous]');
    const next = this.querySelector('[data-history-gallery-next]');
    const phone = this.#phoneQuery.matches;
    if (previous) previous.disabled = phone && index === 0;
    if (next) next.disabled = phone && index === items.length - 1;
  };

  #syncFrame = 0;

  #setupPhoneNav() {
    const items = this.#uniqueItems();
    this.classList.toggle('is-single', items.length < 2);
    if (items.length < 2) return;

    this.#count = document.createElement('span');
    this.#count.className = 'history-gallery__count';
    this.#count.setAttribute('aria-live', 'polite');
    this.querySelector('[data-history-gallery-previous]')?.before(this.#count);

    this.#track.addEventListener('scroll', () => {
      if (!this.#syncFrame) this.#syncFrame = requestAnimationFrame(this.#syncPhoneNav);
    }, { passive: true });
    this.#phoneQuery.addEventListener('change', this.#syncPhoneNav);
    this.#syncPhoneNav();
  }

  disconnectedCallback() {
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#mobileQuery.removeEventListener('change', this.#setMode);
    this.#lightbox?.destroy();
  }

  #bindEvents() {
    const uniqueCount = Math.ceil(this.#items.length / 2);
    this.#modalItems = this.#items.slice(0, uniqueCount).filter((item) => item.getAttribute('aria-hidden') !== 'true');

    for (const [index, item] of this.#items.entries()) {
      if (index >= uniqueCount || item.getAttribute('aria-hidden') === 'true') continue;
      item.setAttribute('role', 'button');
      item.setAttribute('tabindex', '0');
      item.setAttribute('aria-label', this.dataset.openLabel || item.querySelector('img')?.alt || 'Open image in full screen');
      item.addEventListener('click', () => this.#openDialog(item));
      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.#openDialog(item);
      });
    }

    this.querySelector('[data-history-gallery-previous]')?.addEventListener('click', () => this.#moveBy(-1));
    this.querySelector('[data-history-gallery-next]')?.addEventListener('click', () => this.#moveBy(1));
    this.addEventListener('mouseenter', () => { this.#paused = true; });
    this.addEventListener('mouseleave', () => { this.#paused = false; });
    this.addEventListener('focusin', () => { this.#paused = true; });
    this.addEventListener('focusout', () => { this.#paused = false; });
    this.#mobileQuery.addEventListener('change', this.#setMode);

  }

  #openDialog(item) {
    if (!this.#modalItems.length) return;
    const index = Math.max(0, this.#modalItems.indexOf(item));
    this.#lightbox ??= new HistoryLightbox({
      title: this.dataset.title || (this.getAttribute('aria-label') || '').split(':').pop().trim(),
      images: this.#modalItems.map((entry) => entry.querySelector('img')),
      labels: {
        close: this.dataset.closeLabel || 'Close',
        previous: this.querySelector('[data-history-gallery-previous]')?.getAttribute('aria-label') || 'Previous image',
        next: this.querySelector('[data-history-gallery-next]')?.getAttribute('aria-label') || 'Next image',
      },
      onClose: () => { this.#paused = false; item.focus?.({ preventScroll: true }); },
    });
    this.#paused = true;
    this.#lightbox.open(index, item.querySelector('img'));
  }

  #setMode = () => {
    if (this.#raf) {
      cancelAnimationFrame(this.#raf);
      this.#raf = null;
    }

    this.#position = 0;
    this.#motion = null;
    this.#track.style.transform = 'none';
    this.#paused = this.#mobileQuery.matches;
    if (!this.#mobileQuery.matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#startLoop();
    }
  };

  #measureResetPoint() {
    const duplicateStart = Math.floor(this.#items.length / 2);
    return this.#items[duplicateStart]?.offsetLeft || this.#track.scrollWidth / 2;
  }

  #moveBy(direction) {
    const first = this.#items[0];
    const second = this.#items[1];
    if (!first) return;

    if (this.#phoneQuery.matches) {
      const items = this.#uniqueItems();
      const target = items[Math.min(items.length - 1, Math.max(0, this.#phoneIndex() + direction))];
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.#track.scrollTo({ left: target.offsetLeft - items[0].offsetLeft, behavior: still ? 'instant' : 'smooth' });
      return;
    }

    const itemDistance = second ? second.offsetLeft - first.offsetLeft : first.getBoundingClientRect().width;
    const distance = Math.max(itemDistance, this.clientWidth * 0.78);
    if (this.#mobileQuery.matches) {
      this.#track.scrollBy({ left: distance * direction, behavior: 'smooth' });
      return;
    }

    this.#resetPoint = this.#measureResetPoint();
    if (this.#resetPoint <= 0) return;

    const travelDistance = Math.min(distance, this.#resetPoint);
    let startPosition = this.#normalizePosition(this.#position);
    let targetPosition = startPosition - (travelDistance * direction);

    if (targetPosition > 0) {
      startPosition -= this.#resetPoint;
      targetPosition -= this.#resetPoint;
    }

    this.#position = startPosition;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#position = this.#normalizePosition(targetPosition);
      this.#track.style.transform = `translate3d(${this.#position}px, 0, 0)`;
      return;
    }

    this.#motion = {
      start: startPosition,
      target: targetPosition,
      startTime: performance.now(),
      duration: 780
    };
  }

  #normalizePosition(position) {
    if (this.#resetPoint <= 0) return position;

    let normalizedPosition = position;
    while (normalizedPosition <= -this.#resetPoint) normalizedPosition += this.#resetPoint;
    while (normalizedPosition > 0) normalizedPosition -= this.#resetPoint;
    return normalizedPosition;
  }

  #easeOutQuint(progress) {
    return 1 - Math.pow(1 - progress, 5);
  }

  #startLoop() {
    const tick = () => {
      this.#resetPoint = this.#measureResetPoint();
      if (this.#motion) {
        const elapsed = performance.now() - this.#motion.startTime;
        const progress = Math.min(elapsed / this.#motion.duration, 1);
        const easedProgress = this.#easeOutQuint(progress);
        this.#position = this.#motion.start + ((this.#motion.target - this.#motion.start) * easedProgress);

        if (progress >= 1) {
          this.#position = this.#normalizePosition(this.#position);
          this.#motion = null;
        }

        this.#track.style.transform = `translate3d(${this.#position}px, 0, 0)`;
      } else if (!this.#paused && this.#resetPoint > 0) {
        this.#position -= 0.45;
        if (Math.abs(this.#position) >= this.#resetPoint) this.#position += this.#resetPoint;
        this.#track.style.transform = `translate3d(${this.#position}px, 0, 0)`;
      }
      this.#raf = requestAnimationFrame(tick);
    };
    this.#raf = requestAnimationFrame(tick);
  }
}

if (!customElements.get('history-gallery')) {
  customElements.define('history-gallery', HistoryGallery);
}

const revealHistoryCards = (root = document) => {
  const cards = root.querySelectorAll('.history-timeline__card:not([data-history-reveal-ready])');
  if (!cards.length) return;

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    }
  }, { threshold: 0.15, rootMargin: '0px 0px -48px 0px' });

  for (const card of cards) {
    card.dataset.historyRevealReady = 'true';
    observer.observe(card);
  }
};

revealHistoryCards();
document.addEventListener('shopify:section:load', (event) => revealHistoryCards(event.target));
})();
